import { Zap, Star, Shield, CreditCard, Users, Bell, Wand2, Package } from 'lucide-react';
import SEOHead from '@/components/SEOHead';

const releases = [
  {
    version: 'v1.5',
    date: 'May 2026',
    tag: 'Latest',
    tagColor: 'bg-orange-500/15 border-orange-500/30 text-orange-400',
    items: [
      { icon: Bell,    text: 'In-app notification centre — generation complete, referral credited, payment confirmed' },
      { icon: Shield,  text: 'Cookie consent banner with granular analytics / marketing controls' },
      { icon: Shield,  text: 'Acceptable Use Policy, Affiliate Terms & Conditions, and Cookie Policy pages' },
      { icon: Users,   text: 'Affiliate programme dashboard with tier-based commissions (Bronze / Silver / Gold)' },
      { icon: Zap,     text: 'Live "websites generated" counter on the homepage' },
    ],
  },
  {
    version: 'v1.4',
    date: 'April 2026',
    tag: 'Stable',
    tagColor: 'bg-blue-500/15 border-blue-500/30 text-blue-400',
    items: [
      { icon: Wand2,   text: 'AI prompt enhancer — automatically rewrites vague prompts for higher-quality output' },
      { icon: Package, text: 'Templates library — 50+ pre-built website templates with search and category filters' },
      { icon: Zap,     text: 'Prompt-to-website streaming via SSE — see your site build line-by-line in real time' },
      { icon: Users,   text: 'Referral system — earn +10 credits for every friend who signs up with your code' },
      { icon: Shield,  text: 'DOMPurify HTML sanitisation on all AI-generated output' },
    ],
  },
  {
    version: 'v1.3',
    date: 'March 2026',
    tag: 'Stable',
    tagColor: 'bg-blue-500/15 border-blue-500/30 text-blue-400',
    items: [
      { icon: CreditCard, text: 'Flutterwave integration — cards, mobile money and bank transfer payments' },
      { icon: CreditCard, text: 'Crypto payments via NowPayments.io — BTC, ETH, USDT, SOL and 300+ coins' },
      { icon: Zap,        text: 'Yearly billing plans — up to 20% savings vs monthly' },
      { icon: Star,       text: 'Admin dashboard — user management, credit top-ups, subscription overrides' },
    ],
  },
  {
    version: 'v1.2',
    date: 'February 2026',
    tag: 'Stable',
    tagColor: 'bg-blue-500/15 border-blue-500/30 text-blue-400',
    items: [
      { icon: Zap,  text: 'Version history — snapshot every AI iteration and roll back with one click' },
      { icon: Zap,  text: 'Custom domain deployment via Vercel API' },
      { icon: Star, text: 'ZIP export for paid subscribers — download full HTML/CSS/JS project' },
      { icon: Star, text: 'API key management — build on BuildersLab programmatically' },
      { icon: Bell, text: 'Full transactional email suite (welcome, generation complete, low credits, referral)' },
    ],
  },
  {
    version: 'v1.1',
    date: 'January 2026',
    tag: 'Stable',
    tagColor: 'bg-blue-500/15 border-blue-500/30 text-blue-400',
    items: [
      { icon: Users,   text: 'User dashboard — website history, credit balance, subscription management' },
      { icon: Shield,  text: 'Account settings — profile, password change, data deletion' },
      { icon: Zap,     text: 'Daily credit reset cron — free-tier credits renew every 24 hours' },
      { icon: Star,    text: 'Subscription tiers — Free (5/day), Starter (20/day), Pro (50/day), Agency (150/day)' },
      { icon: Package, text: 'Website preview page with device toggle (desktop / tablet / mobile)' },
    ],
  },
  {
    version: 'v1.0',
    date: 'December 2025',
    tag: 'Launch',
    tagColor: 'bg-zinc-700/50 border-zinc-600 text-zinc-300',
    items: [
      { icon: Wand2,  text: 'Initial launch — AI website builder powered by Claude 4 Sonnet' },
      { icon: Shield, text: 'Secure email + password authentication' },
      { icon: Zap,    text: 'Real-time HTML/CSS/JS generation with streaming output' },
      { icon: Star,   text: 'Image attachment — describe and attach a screenshot for layout reference' },
      { icon: Users,  text: 'Voice input — dictate your prompt with the built-in microphone' },
    ],
  },
];

const CHANGELOG_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'BuildersLab Changelog',
  url: 'https://builderslab.net/Changelog',
  description: "A running history of every feature, fix, and improvement shipped to BuildersLab's AI website builder.",
  dateModified: '2026-05-04',
  publisher: { '@type': 'Organization', name: 'BuildersLab', url: 'https://builderslab.net' },
};

export default function Changelog() {
  return (
    <div className="min-h-screen bg-zinc-950 pt-24 pb-20">
      <SEOHead
        title="Changelog — BuildersLab"
        description="See every feature, fix, and improvement shipped to BuildersLab's AI website builder."
        ogUrl="https://builderslab.net/Changelog"
        noIndex={false}
        jsonLd={CHANGELOG_JSONLD}
      />
      <div className="max-w-3xl mx-auto px-6">

        <div
         
         
         
          className="mb-16"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400 text-xs font-semibold uppercase tracking-wider mb-6">
            Product Updates
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4 tracking-tight">
            Changelog
          </h1>
          <p className="text-zinc-400 text-base leading-relaxed max-w-xl">
            Every feature shipped, fix landed, and improvement made — in plain English.
          </p>
        </div>

        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-[7px] top-2 bottom-0 w-px bg-zinc-800" />

          <div className="space-y-14">
            {releases.map((release, i) => (
              <div
                key={release.version}
               
               
               
                className="relative pl-8"
              >
                {/* Timeline dot */}
                <div className="absolute left-0 top-[6px] w-3.5 h-3.5 rounded-full bg-zinc-800 border-2 border-orange-500/60" />

                <div className="flex flex-wrap items-center gap-3 mb-5">
                  <span className="text-white font-black text-xl">{release.version}</span>
                  <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold border ${release.tagColor}`}>
                    {release.tag}
                  </span>
                  <span className="text-zinc-500 text-sm">{release.date}</span>
                </div>

                <ul className="space-y-3">
                  {release.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-3 text-zinc-400 text-[15px] leading-relaxed">
                      <item.icon className="w-4 h-4 text-orange-400/70 shrink-0 mt-0.5" />
                      {item.text}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
